import { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';

const APP_URL = 'https://app.theonestack.com/build';

const tiers = [
  {
    name: 'Starter',
    monthly: 99,
    annual: 79,
    desc: 'For small contractors getting organized.',
    features: [
      'Up to 10 active jobs',
      'Estimates & proposals',
      'Basic contract templates',
      'Document storage (5 GB)',
      'Sub portal (up to 10 subs)',
      'Email support',
    ],
    popular: false,
  },
  {
    name: 'Pro',
    monthly: 199,
    annual: 159,
    desc: 'Full-featured for growing contractors. Unlimited everything.',
    features: [
      'Unlimited active jobs',
      'Estimates, proposals & contracts',
      'E-signatures',
      'Change orders & lien waivers',
      'Unlimited subcontractors + portal',
      'Document storage (50 GB)',
      'Reports & analytics',
      'Priority support',
    ],
    popular: true,
  },
  {
    name: 'Enterprise',
    monthly: 499,
    annual: 399,
    desc: 'Multi-office teams with advanced needs.',
    features: [
      'Everything in Pro',
      'Multi-office / multi-entity',
      'Unlimited document storage',
      'Custom contract templates',
      'API access',
      'SSO integration',
      'Dedicated account manager',
      'Custom training & onboarding',
    ],
    popular: false,
  },
];

export function PricingSection() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="relative py-28 sm:py-32">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(17,17,17,0) 0%, rgba(26,26,26,0.3) 50%, rgba(17,17,17,0) 100%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="mb-16 text-center reveal">
          <span className="mb-4 inline-block rounded-full border border-build-700/40 bg-build-900/40 px-3 py-1 text-xs font-medium text-orange-400">
            Pricing
          </span>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Simple, Honest Pricing
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-zinc-400">
            Start free for 14 days. No credit card required. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3">
            <span className={`text-sm ${!annual ? 'text-zinc-100' : 'text-zinc-500'}`}>Monthly</span>
            <button
              className={`billing-toggle ${annual ? 'active' : ''}`}
              onClick={() => setAnnual(!annual)}
              aria-label="Toggle annual billing"
            />
            <span className={`text-sm ${annual ? 'text-zinc-100' : 'text-zinc-500'}`}>
              Annual
            </span>
            {annual && (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">
                2 months free
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`reveal relative rounded-2xl border p-8 ${
                tier.popular
                  ? 'pricing-popular border-orange-500/40 bg-build-900/40'
                  : 'border-build-700/30 bg-build-900/20'
              }`}
            >
              {tier.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-orange-500 px-3 py-1 text-xs font-medium text-white">
                  Most Popular
                </span>
              )}
              <h3 className="mb-1 text-xl font-bold">{tier.name}</h3>
              <p className="mb-6 text-sm text-zinc-400">{tier.desc}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">
                  ${annual ? tier.annual : tier.monthly}
                </span>
                <span className="text-zinc-400">/mo</span>
                {annual && (
                  <span className="ml-2 text-xs text-zinc-600 line-through">
                    ${tier.monthly}/mo
                  </span>
                )}
              </div>
              <ul className="mb-8 space-y-3">
                {tier.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />
                    {feat}
                  </li>
                ))}
              </ul>
              <a
                href={APP_URL}
                className={`group flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold transition ${
                  tier.popular
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'border border-build-700/40 text-zinc-300 hover:bg-build-900/60'
                }`}
              >
                Start Free Trial
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
